/* ================================================================
   Kempenvision — Web MIDI + action registry + LED feedback
   Ondersteunt APC Mini (mk1/mk2) + AKAI LPD8.
================================================================ */

/* --------- ACTIONS --------- */
const _viewActions = ['idle','song','ceremony','public_reveal','results']
  .map(v => ({ key: `view.${v}`, label: `View: ${v}`, group: 'View' }))
const _songNav = [
  { key: 'song.toggle',   label: 'Song: Play/Pause',   group: 'Song' },
  { key: 'song.play',     label: 'Song: Play',         group: 'Song' },
  { key: 'song.pause',    label: 'Song: Pause',        group: 'Song' },
  { key: 'song.next',     label: 'Song: Volgende',     group: 'Song' },
  { key: 'song.prev',     label: 'Song: Vorige',       group: 'Song' },
  { key: 'song.restart',  label: 'Song: Herstart 0:00',group: 'Song' },
  { key: 'song.seek.minus5', label: 'Song: −5s',       group: 'Song' },
  { key: 'song.seek.plus5',  label: 'Song: +5s',       group: 'Song' },
]
const _songKnobs = [
  { key: 'song.volume',   label: 'Song: Volume (knob)', group: 'Song knobs' },
  { key: 'song.seek.abs', label: 'Song: Scrub (knob)',  group: 'Song knobs' },
]
const _entryGoto = Array.from({ length: 12 }, (_, i) => ({
  key: `entry.goto.${i + 1}`, label: `Entry: Direct #${i + 1}`, group: 'Entry direct'
}))
const _ceremonyNav = [
  { key: 'ceremony.next',  label: 'Ceremony: Volgende punten', group: 'Ceremony (jury)' },
  { key: 'ceremony.prev',  label: 'Ceremony: Terug',           group: 'Ceremony (jury)' },
  { key: 'ceremony.reset', label: 'Ceremony: Reset',           group: 'Ceremony (jury)' },
]
const _ceremonySteps = Array.from({ length: 12 }, (_, i) => ({
  key: `ceremony.step.${i}`, label: `Ceremony: Stap ${i}`, group: 'Ceremony stap'
}))
const _publicRevealNav = [
  { key: 'public_reveal.next',  label: 'Publiek reveal: Volgende', group: 'Publiek reveal' },
  { key: 'public_reveal.prev',  label: 'Publiek reveal: Terug',    group: 'Publiek reveal' },
  { key: 'public_reveal.reset', label: 'Publiek reveal: Reset',    group: 'Publiek reveal' },
]
const _publicRevealSteps = Array.from({ length: 12 }, (_, i) => ({
  key: `public_reveal.step.${i}`, label: `Publiek reveal: Stap ${i}`, group: 'Publiek reveal stap'
}))
const _genreActions = [
  { key: 'genre.clear',     label: 'Genre: uit entry',   group: 'Genre' },
  { key: 'genre.edm',       label: 'Genre: EDM',         group: 'Genre' },
  { key: 'genre.rock',      label: 'Genre: Rock',        group: 'Genre' },
  { key: 'genre.pop',       label: 'Genre: Pop',         group: 'Genre' },
  { key: 'genre.ballad',    label: 'Genre: Ballad',      group: 'Genre' },
  { key: 'genre.hiphop',    label: 'Genre: Hiphop',      group: 'Genre' },
  { key: 'genre.synthwave', label: 'Genre: Synthwave',   group: 'Genre' },
]
const _phaseActions = ['setup','live_songs','jury_reveal','public_vote','public_reveal','finished']
  .map(p => ({ key: `phase.${p}`, label: `Phase: ${p}`, group: 'Phase' }))
const _phaseNav = [
  { key: 'phase.next', label: 'Phase: Volgende',  group: 'Phase' },
  { key: 'phase.prev', label: 'Phase: Vorige',    group: 'Phase' },
]
const _showToggles = [
  { key: 'submissions.toggle', label: 'Inschrijvingen aan/uit', group: 'Show' },
  { key: 'vote.close',         label: 'Publiek stemmen sluiten', group: 'Show' },
  { key: 'emergency.blackout', label: 'Emergency blackout',     group: 'Show' },
]

export const ACTIONS = [
  ..._viewActions,
  ..._songNav, ..._songKnobs,
  ..._entryGoto,
  ..._ceremonyNav, ..._ceremonySteps,
  ..._publicRevealNav, ..._publicRevealSteps,
  ..._genreActions,
  ..._phaseActions, ..._phaseNav,
  ..._showToggles,
]
export const ACTION_GROUPS = [
  'View','Song','Song knobs','Entry direct',
  'Ceremony (jury)','Ceremony stap',
  'Publiek reveal','Publiek reveal stap',
  'Genre','Phase','Show',
]
export const PHASE_ORDER = ['setup','live_songs','jury_reveal','public_vote','public_reveal','finished']

/* --------- Web MIDI wrapper --------- */
export async function openMIDI() {
  if (!navigator.requestMIDIAccess) throw new Error('Web MIDI API niet beschikbaar (Chrome/Edge vereist)')
  return navigator.requestMIDIAccess({ sysex: false })
}
export function listInputs(access)  { return Array.from(access.inputs.values()) }
export function listOutputs(access) { return Array.from(access.outputs.values()) }
export function attachInputListener(input, onEvent) {
  const handler = (msg) => {
    const [status, data1 = 0, data2 = 0] = msg.data
    const type = status & 0xF0
    const channel = status & 0x0F
    if (type === 0x80) return
    if (type === 0x90 && data2 === 0) return
    onEvent({ type, channel, data1, data2, raw: Array.from(msg.data) })
  }
  input.onmidimessage = handler
  return () => { input.onmidimessage = null }
}

export function eventKey(evt) {
  if (!evt) return null
  if (evt.type === 0x90) return `n-${evt.channel}:${evt.data1}`
  if (evt.type === 0xB0) return `c-${evt.channel}:${evt.data1}`
  return null
}
export function eventLabel(evt) {
  if (!evt) return ''
  const ch = evt.channel + 1
  const note = noteName(evt.data1)
  if (evt.type === 0x90) return `Note ${note} ch${ch} · v${evt.data2}`
  if (evt.type === 0xB0) return `CC #${evt.data1} ch${ch} · v${evt.data2}`
  return `0x${evt.type.toString(16)} ch${ch}`
}
export function keyLabel(key) {
  if (!key) return '—'
  const m = key.match(/^([nc])-(\d+):(\d+)$/)
  if (!m) return key
  const [, kind, ch, num] = m
  if (kind === 'n') return `Note ${noteName(+num)} ch${+ch + 1}`
  return `CC #${num} ch${+ch + 1}`
}
function noteName(n) {
  const names = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B']
  return `${names[n % 12]}${Math.floor(n / 12) - 1}`
}
export function keyIsNote(key) { return typeof key === 'string' && key.startsWith('n-') }
export function keyIsCC(key)   { return typeof key === 'string' && key.startsWith('c-') }
export function keyToNoteNum(key) {
  const m = /^n-(\d+):(\d+)$/.exec(key || '')
  return m ? { channel: Number(m[1]), num: Number(m[2]) } : null
}

/* --------- LED palette (gedeeld met imagecompo) --------- */
const _solid = [
  ['off','Uit','#333'], ['white','Wit','#ffffff'],
  ['red','Rood','#ff3838'], ['orange','Oranje','#ff9500'],
  ['yellow','Geel','#ffcf40'], ['lime','Lime','#c8ff2b'],
  ['green','Groen','#3adf3a'], ['cyan','Cyaan','#00d9ff'],
  ['blue','Blauw','#337cff'], ['purple','Paars','#8b3fff'],
  ['magenta','Magenta','#ff2b8f'], ['pink','Roze','#ff7ac8'],
]
export const LED_COLORS = Object.fromEntries([
  ..._solid.map(([k, label, swatch]) => [k, { label, swatch }]),
  ..._solid.filter(([k]) => k !== 'off')
           .map(([k, label, swatch]) => [`${k}_blink`, { label: `${label} (knipper)`, swatch }]),
])
export const LED_COLOR_KEYS = Object.keys(LED_COLORS)

const _mk1Buckets = {
  green:  ['green','lime','cyan'],
  red:    ['red','orange','blue','purple','magenta','pink'],
  yellow: ['yellow','white'],
}
function _mk1Vel(color) {
  const solids = { green: 1, red: 3, yellow: 5 }
  const blinks = { green: 2, red: 4, yellow: 6 }
  const base = color.replace(/_blink$/,'')
  const bucket = Object.entries(_mk1Buckets).find(([, arr]) => arr.includes(base))?.[0] || 'red'
  return color.endsWith('_blink') ? blinks[bucket] : solids[bucket]
}
const _mk1Palette = { off: { ch: 0, vel: 0 } }
LED_COLOR_KEYS.forEach(k => { if (k !== 'off') _mk1Palette[k] = { ch: 0, vel: _mk1Vel(k) } })

const _mk2Indices = {
  white: 1, red: 3, orange: 9, yellow: 13, lime: 17, green: 21,
  cyan: 33, blue: 45, purple: 49, magenta: 53, pink: 57,
}
const _mk2Palette = { off: { ch: 0, vel: 0 } }
for (const [name, idx] of Object.entries(_mk2Indices)) {
  _mk2Palette[name]            = { ch: 6,  vel: idx }
  _mk2Palette[`${name}_blink`] = { ch: 14, vel: idx }
}

export const DEVICE_MODELS = {
  mk1:  { label: 'APC Mini (mk1) — 3 kleuren', hasLeds: true, palette: _mk1Palette },
  mk2:  { label: 'APC Mini mk2 — RGB',         hasLeds: true, palette: _mk2Palette },
  lpd8: { label: 'AKAI LPD8 — 8 pads + 8 knobs (geen LEDs)', hasLeds: false, palette: {} },
}
export const DEVICE_MODEL_KEYS = Object.keys(DEVICE_MODELS)
function getModel(name) { return DEVICE_MODELS[name] || DEVICE_MODELS.mk2 }
export function modelHasLeds(name) { return !!getModel(name).hasLeds }

export function sendLED(output, key, colorName, modelName) {
  if (!output || !keyIsNote(key)) return
  const model = getModel(modelName || loadModel())
  if (!model.hasLeds) return
  const n = keyToNoteNum(key); if (!n) return
  const p = model.palette[colorName] || model.palette.off
  try { output.send([0x90 | (p.ch & 0x0F), n.num & 0x7F, p.vel & 0x7F]) } catch {}
}
export function clearAllLeds(output, modelName) {
  if (!output) return
  const model = getModel(modelName || loadModel())
  if (!model.hasLeds) return
  for (let n = 0; n <= 89; n++) {
    try { output.send([0x90, n & 0x7F, 0]) } catch {}
  }
}

/* --------- Normalized mappings --------- */
export function normalizeMapping(v) {
  if (typeof v === 'string') return { action: v, led_idle: 'off', led_active: 'off' }
  return {
    action:     v?.action || '',
    led_idle:   v?.led_idle   || 'off',
    led_active: v?.led_active || 'off',
  }
}

export function evalActive(action, ctx = {}) {
  if (!action) return false
  const state = ctx.state; const show = ctx.show
  const entries = ctx.entries || []

  if (action.startsWith('view.'))  return state?.current_view === action.slice(5)
  if (action.startsWith('phase.') && !['phase.next','phase.prev'].includes(action)) {
    return show?.phase === action.slice(6)
  }
  if (action.startsWith('entry.goto.')) {
    const n = Number(action.slice(11))
    return !!entries[n - 1] && state?.current_entry_id === entries[n - 1].id
  }
  if (action.startsWith('ceremony.step.'))       return state?.ceremony_step       === Number(action.slice(14))
  if (action.startsWith('public_reveal.step.'))  return state?.public_reveal_step  === Number(action.slice(19))
  if (action === 'song.play' || action === 'song.toggle') return !!state?.audio_playing
  if (action === 'song.pause') return !state?.audio_playing
  if (action === 'submissions.toggle') return !!show?.submissions_open
  if (action.startsWith('genre.') && action !== 'genre.clear')
    return state?.genre_override === action.slice(6)
  if (action === 'genre.clear') return !state?.genre_override
  return false
}

export function applyLeds(output, mappings, ctx, modelName) {
  if (!output || !mappings) return
  const model = modelName || loadModel()
  for (const [key, raw] of Object.entries(mappings)) {
    if (!keyIsNote(key)) continue
    const m = normalizeMapping(raw)
    const on = evalActive(m.action, ctx)
    sendLED(output, key, on ? m.led_active : m.led_idle, model)
  }
}

/* --------- Persistence --------- */
const KEY_MAP     = 'kempenvision-midi-mappings'
const KEY_INPUT   = 'kempenvision-midi-input-id'
const KEY_OUTPUT  = 'kempenvision-midi-output-id'
const KEY_ACTIVE  = 'kempenvision-midi-active'
const KEY_MODEL   = 'kempenvision-midi-model'
export function loadModel()     { try { return localStorage.getItem(KEY_MODEL) || 'mk2' } catch { return 'mk2' } }
export function saveModel(name) { try { localStorage.setItem(KEY_MODEL, name || 'mk2') } catch {} }
export function loadMappings() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY_MAP)) ?? {}
    const out = {}
    for (const [k, v] of Object.entries(raw)) out[k] = normalizeMapping(v)
    return out
  } catch { return {} }
}
export function saveMappings(m) { localStorage.setItem(KEY_MAP, JSON.stringify(m)) }
export function loadInputId()   { return localStorage.getItem(KEY_INPUT) ?? '' }
export function saveInputId(id) { localStorage.setItem(KEY_INPUT, id ?? '') }
export function loadOutputId()  { return localStorage.getItem(KEY_OUTPUT) ?? '' }
export function saveOutputId(id){ localStorage.setItem(KEY_OUTPUT, id ?? '') }
export function loadMidiActive(){ return localStorage.getItem(KEY_ACTIVE) === '1' }
export function saveMidiActive(v){ localStorage.setItem(KEY_ACTIVE, v ? '1' : '0') }

/* --------- Action dispatcher ---------
   value (0-127) is optioneel: alleen voor CC-driven acties (knobs/faders).
*/
export async function runAction(action, ctx, value) {
  const { data, patchState, patchShow } = ctx
  const state = data?.state
  if (!state) return
  const entries = data.entries ?? []
  const idx = entries.findIndex(e => e.id === state.current_entry_id)
  const show = data.show

  const gotoEntry = (i) => patchState({
    current_entry_id: entries[i].id, genre_override: null,
    audio_playing: false, audio_position: 0,
  })
  const currentDuration = data?.current_entry?.duration_s ?? state.audio_duration ?? 0

  switch (action) {
    // View
    case 'view.idle':          return patchState({ current_view: 'idle' })
    case 'view.song':          return patchState({ current_view: 'song' })
    case 'view.ceremony':      return patchState({ current_view: 'ceremony' })
    case 'view.public_reveal': return patchState({ current_view: 'public_reveal' })
    case 'view.results':       return patchState({ current_view: 'results' })
    // Emergency
    case 'emergency.blackout':
      return patchState({ current_view: 'idle', audio_playing: false })
    // Song transport
    case 'song.toggle':   return patchState({ audio_playing: !state.audio_playing })
    case 'song.play':     return patchState({ audio_playing: true })
    case 'song.pause':    return patchState({ audio_playing: false })
    case 'song.next':     if (idx >= 0 && idx < entries.length - 1) return gotoEntry(idx + 1); break
    case 'song.prev':     if (idx > 0) return gotoEntry(idx - 1); break
    case 'song.restart':  return patchState({ audio_position: 0 })
    case 'song.seek.plus5':  return patchState({ audio_position: Math.max(0, (state.audio_position || 0) + 5) })
    case 'song.seek.minus5': return patchState({ audio_position: Math.max(0, (state.audio_position || 0) - 5) })
    case 'song.volume':
      if (typeof value === 'number') return patchState({ audio_volume: Math.max(0, Math.min(1, value / 127)) })
      break
    case 'song.seek.abs':
      if (typeof value === 'number' && currentDuration > 0) {
        return patchState({ audio_position: (value / 127) * currentDuration })
      }
      break
    // Ceremony (jury)
    case 'ceremony.next':  return patchState({ ceremony_step: (state.ceremony_step ?? 0) + 1 })
    case 'ceremony.prev':  return patchState({ ceremony_step: Math.max(0, (state.ceremony_step ?? 0) - 1) })
    case 'ceremony.reset': return patchState({ ceremony_step: 0 })
    // Public reveal
    case 'public_reveal.next':  return patchState({ public_reveal_step: (state.public_reveal_step ?? 0) + 1 })
    case 'public_reveal.prev':  return patchState({ public_reveal_step: Math.max(0, (state.public_reveal_step ?? 0) - 1) })
    case 'public_reveal.reset': return patchState({ public_reveal_step: 0 })
    // Show toggles
    case 'submissions.toggle': return patchShow({ submissions_open: !show?.submissions_open })
    case 'vote.close':         return patchShow({ vote_open: false })
    // Genre clear
    case 'genre.clear':   return patchState({ genre_override: null })
    default:
      if (action.startsWith('genre.'))              return patchState({ genre_override: action.slice(6) })
      if (action.startsWith('entry.goto.')) {
        const n = Number(action.slice(11))
        if (entries[n - 1]) return gotoEntry(n - 1)
        break
      }
      if (action.startsWith('ceremony.step.'))      return patchState({ ceremony_step: Number(action.slice(14)) })
      if (action.startsWith('public_reveal.step.')) return patchState({ public_reveal_step: Number(action.slice(19)) })
      // Phase.<X> uitgezonderd next/prev
      if (action === 'phase.next' || action === 'phase.prev') {
        const cur = show?.phase; const list = PHASE_ORDER
        const i = list.indexOf(cur)
        const nxt = action === 'phase.next' ? Math.min(list.length - 1, i + 1) : Math.max(0, i - 1)
        return patchShow({ phase: list[nxt] })
      }
      if (action.startsWith('phase.')) return patchShow({ phase: action.slice(6) })
  }
}
